/**
 * Super Admin — catálogo GLOBAL de tipos de medio (outlet_kinds).
 *
 * Clasifican los medios del módulo Medios Tradicionales en todas las cuentas
 * agencia (ej. "Diarios nacionales", "Radios Arequipa Metropolitana"). El
 * delete es soft (desactivar): los medios históricos referencian el código.
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Check, Radio, RotateCcw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api, type OutletKindOut } from "@/lib/api";

export const Route = createFileRoute("/_auth/admin/outlet-kinds")({
  component: AdminOutletKinds,
});

const FILE_KINDS = ["pdf", "jpeg", "png", "gif", "webp", "mp3", "wav", "ogg", "mp4", "mov", "avi", "webm", "xlsx", "csv", "docx", "txt", "zip"];

const EMPTY_FORM = { code: "", name: "", description: "", accepts_formats: [] as string[], sort_order: "0" };

function AdminOutletKinds() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["admin", "outlet-kinds"],
    queryFn: () => api.admin.listOutletKinds(),
  });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["admin", "outlet-kinds"] });
    qc.invalidateQueries({ queryKey: ["media", "outlet-kinds"] });
  }

  const create = useMutation({
    mutationFn: () => api.admin.createOutletKind({
      code: form.code,
      name: form.name,
      description: form.description || undefined,
      accepts_formats: form.accepts_formats,
      sort_order: Number(form.sort_order) || 0,
    }),
    onSuccess: () => { invalidate(); toast.success("Tipo de medio creado"); reset(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof api.admin.patchOutletKind>[1] }) =>
      api.admin.patchOutletKind(id, body),
    onSuccess: () => { invalidate(); toast.success("Tipo de medio actualizado"); reset(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.admin.deleteOutletKind(id),
    onSuccess: () => { invalidate(); toast.success("Tipo de medio desactivado"); },
    onError: (e: Error) => toast.error(e.message),
  });

  const reactivate = useMutation({
    mutationFn: (id: number) => api.admin.patchOutletKind(id, { is_active: true }),
    onSuccess: () => { invalidate(); toast.success("Tipo de medio reactivado"); },
    onError: (e: Error) => toast.error(e.message),
  });

  function reset() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function startEdit(item: OutletKindOut) {
    setEditingId(item.id);
    setForm({
      code: item.code,
      name: item.name,
      description: item.description ?? "",
      accepts_formats: item.accepts_formats ?? [],
      sort_order: String(item.sort_order),
    });
    setShowForm(true);
  }

  function toggleFormat(f: string) {
    setForm({
      ...form,
      accepts_formats: form.accepts_formats.includes(f)
        ? form.accepts_formats.filter(x => x !== f)
        : [...form.accepts_formats, f],
    });
  }

  function submit() {
    const body = {
      code: form.code,
      name: form.name,
      description: form.description || undefined,
      accepts_formats: form.accepts_formats,
      sort_order: Number(form.sort_order) || 0,
    };
    if (editingId) {
      patch.mutate({ id: editingId, body });
    } else {
      create.mutate();
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Radio size={18} className="text-brand-600" /> Tipos de medios
          </h2>
          <p className="text-xs text-ink-500">
            Catálogo global del módulo Medios Tradicionales. Clasifica los medios en
            todas las cuentas agencia (tab Medios). Los formatos marcados son el
            default de evidencias al crear un medio del tipo.
          </p>
        </div>
        {!showForm && (
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus size={14} /> Nuevo tipo
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="mb-4 border-brand-200">
          <CardContent className="py-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Código *</Label>
                <Input
                  value={form.code}
                  disabled={!!editingId}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "_") })}
                  placeholder="radio_cusco"
                />
                <p className="text-[10px] text-ink-400 mt-1">
                  Solo a-z, 0-9, _ y -. Único. {editingId ? "No editable: los medios lo referencian." : ""}
                </p>
              </div>
              <div>
                <Label className="text-xs">Nombre visible *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Radios en Cusco" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Descripción (opcional)</Label>
                <Textarea rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Formatos de evidencia default</Label>
                <div className="flex gap-1.5 flex-wrap mt-1">
                  {FILE_KINDS.map(f => (
                    <button key={f} type="button" onClick={() => toggleFormat(f)}
                      className={`text-[11px] uppercase px-2 py-1 rounded border transition-colors ${
                        form.accepts_formats.includes(f)
                          ? "bg-brand-600 text-white border-brand-600"
                          : "bg-white dark:bg-ink-900 text-ink-500 border-ink-200 dark:border-ink-700 hover:border-brand-300"
                      }`}>
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Orden</Label>
                <Input type="number" value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={reset}>
                <X size={14} /> Cancelar
              </Button>
              <Button onClick={submit} disabled={!form.code || !form.name || create.isPending || patch.isPending}>
                <Check size={14} /> {editingId ? "Guardar" : "Crear"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {list.isLoading ? <Skeleton className="h-40" /> : (
        <div className="space-y-2">
          {(list.data ?? []).map(item => (
            <Card key={item.id} className={!item.is_active ? "opacity-50" : ""}>
              <CardContent className="py-3 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{item.name}</span>
                    <Badge variant="outline" className="font-mono text-[10px]">{item.code}</Badge>
                    {!item.is_active && <Badge variant="outline" className="text-bad">Inactivo</Badge>}
                  </div>
                  {item.description && (
                    <div className="text-xs text-ink-500 mt-0.5">{item.description}</div>
                  )}
                  {item.accepts_formats?.length > 0 && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {item.accepts_formats.map(f => (
                        <span key={f} className="text-[10px] uppercase bg-ink-100 dark:bg-ink-800 px-1.5 py-0.5 rounded">
                          {f}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(item)} title="Editar">
                    <Pencil size={14} />
                  </Button>
                  {item.is_active ? (
                    <Button size="sm" variant="ghost" title="Desactivar"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(item.id)}>
                      <Trash2 size={14} className="text-bad" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" title="Reactivar"
                      disabled={reactivate.isPending}
                      onClick={() => reactivate.mutate(item.id)}>
                      <RotateCcw size={14} className="text-emerald-600" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {!(list.data ?? []).length && !list.isLoading && (
            <div className="text-center py-12 text-ink-500 border-2 border-dashed border-ink-200 rounded-lg">
              Sin tipos de medio. Corre la migración 0019 para seedear los 7 tipos base.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
